import { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";
import DOMPurify from "dompurify";
import { SiHuggingface } from "react-icons/si";

// ── THEME ─────────────────────────────────────────────────────────────────────
const C = {
  bg:      "#1a1a1a",
  bgCard:  "#222222",
  bgDeep:  "#141414",
  orange:  "#E8541A",
  orangeL: "#FF6B35",
  white:   "#FFFFFF",
  gray:    "#AAAAAA",
  grayD:   "#555555",
  border:  "rgba(232,84,26,0.25)",
};
const PX = "'Press Start 2P', monospace";
const SF = "'Inter', sans-serif";

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Inter:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: ${C.bg}; color: ${C.white}; font-family: ${SF}; overflow-x: hidden; }
  ::selection { background: ${C.orange}; color: #fff; }
  ::-webkit-scrollbar { width: 6px; background: ${C.bgDeep}; }
  ::-webkit-scrollbar-thumb { background: ${C.orange}; border-radius: 3px; }
  input, textarea { font-family: ${SF}; outline: none; }

  /* -- LAYOUT CLASSES -- */
  .nav-container { height: 64px; padding: 0 40px; display: flex; align-items: center; justify-content: space-between; }
  .nav-links { display: flex; gap: 2px; }
  .hamburger { display: none; background: transparent; border: none; font-size: 24px; color: ${C.orange}; cursor: pointer; }

  .hero-section { min-height: 100vh; padding: 0 60px 0 100px; display: flex; align-items: center; justify-content: space-between; position: relative; overflow: hidden; }
  .hero-content { max-width: 500px; animation: fadeUp 0.8s ease both; z-index: 10; position: relative; }
  .avatar-wrapper { position: relative; animation: float1 4s ease-in-out infinite; flex-shrink: 0; z-index: 10; }
  .avatar-orb { width: 300px; height: 300px; border-radius: 50%; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; }
  .orb-orbit { position: absolute; top: 50%; left: 50%; width: 320px; height: 320px; margin-left: -160px; margin-top: -160px; border-radius: 50%; border: 1px dashed rgba(232,84,26,0.12); pointer-events: none; }
  .social-sidebar { position: absolute; left: 24px; top: 50%; transform: translateY(-50%); display: flex; flex-direction: column; gap: 10px; z-index: 20; }

  .about-section, .projects-section, .contact-section { min-height: 100vh; padding: 80px 52px; }
  .about-grid { display: grid; grid-template-columns: 220px 1fr; gap: 48px; align-items: start; margin-bottom: 64px; }
  .projects-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
  .contact-main-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: start; max-width: 1000px; margin: 0 auto; }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
  .footer-flex { padding: 20px 52px; display: flex; justify-content: space-between; align-items: center; }
  .hello-bar { width: 28px; height: 2px; background: ${C.orange}; }

  /* -- RESPONSIVE MEDIA QUERIES -- */
  @media (max-width: 768px) {
    .nav-container { padding: 0 20px; }
    .nav-links { display: none; flex-direction: column; position: absolute; top: 64px; left: 0; right: 0; background: rgba(20,20,20,0.96); padding: 20px; border-bottom: 1px solid ${C.border}; backdrop-filter: blur(14px); text-align: center; }
    .nav-links.open { display: flex; }
    .hamburger { display: block; }

    .hero-section { flex-direction: column; justify-content: center; padding: 100px 20px 40px; text-align: center; gap: 40px; }
    .hero-content { display: flex; flex-direction: column; align-items: center; }
    .hello-bar { display: none; }
    .avatar-wrapper { transform: scale(0.85); }
    .social-sidebar { position: static; transform: none; flex-direction: row; justify-content: center; margin-top: 20px; }
    
    .about-section, .projects-section, .contact-section { padding: 60px 20px; }
    .about-grid { grid-template-columns: 1fr; justify-items: center; text-align: center; gap: 32px; }
    .projects-grid { grid-template-columns: repeat(auto-fill, minmax(100%, 1fr)); }
    .contact-main-grid { grid-template-columns: 1fr; gap: 40px; }
    .form-grid { grid-template-columns: 1fr; }
    .footer-flex { flex-direction: column; gap: 12px; text-align: center; padding: 20px; }
  }

  @keyframes float1  { 0%,100%{transform:translateY(0) rotate(12deg)}  50%{transform:translateY(-18px) rotate(22deg)} }
  @keyframes float2  { 0%,100%{transform:translateY(0) rotate(-8deg)}  50%{transform:translateY(-24px) rotate(-18deg)} }
  @keyframes float3  { 0%,100%{transform:translateY(0) rotate(45deg)}  50%{transform:translateY(-14px) rotate(55deg)} }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
  @keyframes slideIn { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:translateX(0)} }
  @keyframes popIn   { 0%{transform:scale(0.6);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
  @keyframes blink   { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes pulse   { 0%,100%{box-shadow:0 0 0 0 rgba(232,84,26,0.4)} 50%{box-shadow:0 0 0 12px rgba(232,84,26,0)} }
  @keyframes spin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  @keyframes walkPx  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
  @keyframes rocketBob { 0%,100%{transform:translateY(0) rotate(-3deg)} 50%{transform:translateY(-14px) rotate(3deg)} }
`;

// ── FLOATING CUBE ─────────────────────────────────────────────────────────────
const Cube = ({ size=40, color=C.orange, style={}, anim="float1", delay="0s" }) => (
  <div style={{
    width:size, height:size,
    background:`linear-gradient(135deg,${color} 0%,rgba(0,0,0,0.45) 100%)`,
    borderRadius:6,
    boxShadow:`${size*.15}px ${size*.15}px 0 rgba(0,0,0,0.55),inset -${size*.1}px -${size*.1}px 0 rgba(0,0,0,0.3),inset ${size*.07}px ${size*.07}px 0 rgba(255,255,255,0.12)`,
    animation:`${anim} ${3.5}s ease-in-out ${delay} infinite`,
    flexShrink:0, ...style,
  }}/>
);

// ── PIXEL AVATAR ──────────────────────────────────────────────────────────────
const PixelAvatar = ({ px=9 }) => {
  const rows = [
    [0,0,1,1,1,1,0,0],
    [0,1,1,1,1,1,1,0],
    [0,1,2,1,1,2,1,0],
    [0,1,1,1,1,1,1,0],
    [0,1,3,3,3,3,1,0],
    [0,0,1,1,1,1,0,0],
    [0,1,4,4,4,4,1,0],
    [0,1,4,4,4,4,1,0],
    [0,1,4,4,4,4,1,0],
    [0,0,1,4,4,1,0,0],
    [0,0,1,4,4,1,0,0],
    [0,0,5,0,0,5,0,0],
  ];
  const pal = {0:"transparent",1:"#C68642",2:"#2244DD",3:"#8B4513",4:C.orange,5:"#333"};
  return (
    <div style={{display:"inline-block",imageRendering:"pixelated",animation:"walkPx 0.9s ease-in-out infinite"}}>
      {rows.map((row,ri)=>(
        <div key={ri} style={{display:"flex"}}>
          {row.map((c,ci)=><div key={ci} style={{width:px,height:px,background:pal[c]}}/>)}
        </div>
      ))}
    </div>
  );
};

// ── PIXEL ROBOT ───────────────────────────────────────────────────────────────
const PixelRobot = ({ px=11 }) => {
  const rows = [
    "001110","011111","010101","011111","001110",
    "000100","011110","111111","110011","111111","011110",
    "010010","010010","110011",
  ];
  return (
    <div style={{display:"inline-block",imageRendering:"pixelated",animation:"rocketBob 2.5s ease-in-out infinite"}}>
      {rows.map((row,ri)=>(
        <div key={ri} style={{display:"flex"}}>
          {row.split("").map((c,ci)=><div key={ci} style={{width:px,height:px,background:c==="1"?C.orange:"transparent"}}/>)}
        </div>
      ))}
    </div>
  );
};

// ── SVG PATHS ─────────────────────────────────────────────────────────────────
const SVGS = {
  github:   "M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12",
  linkedin: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  twitter:  "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.632 5.905-5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  instagram:"M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z",
  youtube:  "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
  email:    "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  html:     "M1.5 0h21l-1.91 21.563L11.977 24l-8.565-2.438L1.5 0zm7.031 9.75l-.232-2.718 10.059.003.23-2.622L5.412 4.41l.698 8.01h9.126l-.326 3.426-2.91.804-2.955-.81-.188-2.11H6.248l.33 4.171L12 19.351l5.379-1.443.744-8.157H8.531z",
  css:      "M1.5 0h21l-1.91 21.563L11.977 24l-8.564-2.438L1.5 0zm17.09 4.413L5.41 4.41l.213 2.622 10.125.002-.255 2.716h-6.64l.24 2.573h6.182l-.366 3.523-2.91.804-2.956-.81-.188-2.11h-2.61l.29 3.855L12 19.288l5.373-1.53L18.59 4.413z",
  js:       "M0 0h24v24H0V0zm22.034 18.276c-.175-1.095-.888-2.015-3.003-2.873-.736-.345-1.554-.585-1.797-1.14-.091-.33-.105-.51-.046-.705.15-.646.915-.84 1.515-.66.39.12.75.42.976.9 1.034-.676 1.034-.676 1.755-1.125-.27-.42-.404-.601-.586-.78-.63-.705-1.469-1.065-2.834-1.034l-.705.089c-.676.165-1.32.525-1.71 1.005-1.14 1.291-.811 3.541.569 4.471 1.365 1.02 3.361 1.244 3.616 2.205.24 1.17-.87 1.545-1.966 1.41-.811-.18-1.26-.586-1.755-1.336l-1.83 1.051c.21.48.45.689.81 1.109 1.74 1.756 6.09 1.666 6.871-1.004.029-.09.24-.705.074-1.65l.046.067zm-8.983-7.245h-2.248c0 1.938-.009 3.864-.009 5.805 0 1.232.063 2.363-.138 2.704-.33.689-1.18.601-1.566.48-.396-.196-.597-.466-.83-.855-.063-.105-.11-.196-.127-.196l-1.825 1.125c.305.63.75 1.172 1.324 1.517.855.51 2.004.675 3.207.405.783-.226 1.458-.691 1.811-1.411.51-.93.402-2.07.397-3.346.012-2.054 0-4.109 0-6.179l.004-.056z",
  react:    "M14.23 12.004a2.236 2.236 0 0 1-2.235 2.236 2.236 2.236 0 0 1-2.236-2.236 2.236 2.236 0 0 1 2.235-2.236 2.236 2.236 0 0 1 2.236 2.236zm2.648-10.69c-1.346 0-3.107.96-4.888 2.622-1.78-1.653-3.542-2.602-4.887-2.602-.41 0-.783.093-1.106.278-1.353.803-1.skipping-2.907 1.353-2.907 1.353 0 3.108.592 4.905 1.'731zm-2.648 28.673c1.346 0 3.107-.96 4.888-2.622 1.78 1.653 3.542 2.602 4.887 2.602.41 0 .783-.093 1.106-.278 1.353-.803 1.skipping-2.907-1.353-2.907-1.353 0-3.108-.592-4.905-1.731zM5.559 8.03c.782-1.353 2.906-1.353 2.906 0 0 3.108-.592 4.905-1.731-.632-1.093-1.351-2.073-2.003-2.907zm12.882 0c.782 1.353-.553 2.907-2.906 2.907 0-3.108.592-4.905 1.731-.632 1.093 1.351 2.073 2.003 2.907zM12 8.164c-1.797 0-3.541.553-4.905 1.731C5.731 11.09 5.138 12.834 5.138 14.63c0 1.797.593 3.541 1.731 4.905 1.364 1.178 3.108 1.731 4.905 1.731s3.541-.553 4.905-1.731c1.138-1.364 1.731-3.108 1.731-4.905 0-1.796-.593-3.54-1.731-4.904C15.541 8.717 13.797 8.164 12 8.164z",
  python:   "M11.914 0C5.82 0 6.2 2.656 6.2 2.656l.007 2.752h5.814v.826H3.9S0 5.789 0 11.969c0 6.18 3.403 5.963 3.403 5.963h2.034v-2.867s-.109-3.402 3.351-3.402h5.766s3.24.052 3.24-3.131V3.333S18.28 0 11.914 0zm-3.21 1.922a1.046 1.046 0 1 1 0 2.093 1.046 1.046 0 0 1 0-2.093zM12.086 24c6.094 0 5.714-2.656 5.714-2.656l-.007-2.752h-5.814v-.826h8.121S24 18.211 24 12.031c0-6.18-3.403-5.963-3.403-5.963h-2.034v2.867s.109 3.402-3.351 3.402H9.446s-3.24-.052-3.24 3.131v5.199S5.72 24 12.086 24zm3.21-1.922a1.046 1.046 0 1 1 0-2.093 1.046 1.046 0 0 1 0 2.093z",
  vscode:   "M0 12C0 5.373 5.373 0 12 0c3.138 0 5.979 1.2 8.095 3.155L8.093 15.157 3.954 11.02 2.54 12.433l5.557 5.557 13.072-13.07A11.953 11.953 0 0 1 24 12c0 6.627-5.373 12-12 12S0 18.627 0 12z",
  node:     "M11.998 24a1.321 1.321 0 0 1-.65-.17L8.12 21.82c-.306-.172-.157-.232-.055-.267.577-.2.693-.245 1.308-.593.064-.037.149-.022.214.017l2.501 1.487a.325.325 0 0 0 .301 0l9.741-5.625a.306.306 0 0 0 .151-.264V7.417a.31.31 0 0 0-.153-.267l-9.739-5.622a.302.302 0 0 0-.3 0L2.353 7.149a.311.311 0 0 0-.154.268v11.25c0 .109.059.211.153.265l2.668 1.54c1.448.724 2.334-.129 2.334-.986V8.29c0-.158.127-.281.285-.281h1.244c.156 0 .285.123.285.281v11.196c0 1.93-1.052 3.037-2.883 3.037-.563 0-1.006 0-2.243-.609L1.35 20.52A1.32 1.32 0 0 1 .698 19.38V8.13A1.326 1.326 0 0 1 1.35 6.99L11.093.275a1.38 1.38 0 0 1 1.312 0l9.741 5.716a1.326 1.326 0 0 1 .654 1.14v11.25a1.33 1.33 0 0 1-.654 1.142l-9.741 5.717a1.322 1.322 0 0 1-.407-.16z",
  sql:      "M12 0C7.373 0 2 1.8 2 5v14c0 3.2 5.373 5 10 5s10-1.8 10-5V5c0-3.2-5.373-5-10-5zm0 2c4.687 0 8 1.57 8 3s-3.313 3-8 3-8-1.57-8-3 3.313-3 8-3zm0 18c-4.687 0-8-1.57-8-3V8.83C5.78 9.854 8.735 10.5 12 10.5s6.22-.646 8-1.67V17c0 1.43-3.313 3-8 3z",
  sklearn:  "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  pandas:   "M9 3H7v7h2V3zm8 0h-2v7h2V3zM9 14H7v7h2v-7zm8 0h-2v7h2v-7zm-4-5h-2v10h2V9z",
  jupyter:  "M14.25 2.175C8.047 2.175 3 7.222 3 13.425S8.047 24.675 14.25 24.675s11.25-5.047 11.25-11.25-5.047-11.25-11.25-11.25zm0 20.25c-4.963 0-9-4.037-9-9s4.037-9 9-9 9 4.037 9 9-4.037 9-9 9zm.75-13.5h-1.5v6h1.5v-6zm0 7.5h-1.5v1.5h1.5v-1.5z",
huggingface: "M18.285 5.715a.57.57 0 0 0-.006 0c-.84 0-1.56.45-1.957 1.122a4.392 4.392 0 0 0-1.662-.325 4.392 4.392 0 0 0-1.663.325A2.25 2.25 0 0 0 11.04 5.73a2.25 2.25 0 0 0-2.25 2.25c0 .195.03.384.074.567A5.25 5.25 0 0 0 6.75 13.5a5.25 5.25 0 0 0 5.25 5.25 5.25 5.25 0 0 0 5.25-5.25 5.25 5.25 0 0 0-2.114-4.953c.044-.183.074-.372.074-.567a2.25 2.25 0 0 0-2.25-2.25zM9 10.5a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zm6 0a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zm-5.25 3h4.5a.375.375 0 0 1 0 .75c-.469.563-1.125.75-2.25.75s-1.781-.188-2.25-.75a.375.375 0 0 1 0-.75z",
};

// ── SOCIAL ICON ───────────────────────────────────────────────────────────────
const SocialIcon = ({ type, href }) => {
  const [hov, setHov] = useState(false);

  const iconMap = {
    huggingface: <SiHuggingface size={17}/>,
    // keep SVG path fallback for others
  };

  return (
    <a href={href} target="_blank" rel="noreferrer"
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        width:38, height:38,
        background:hov?C.orange:"rgba(255,255,255,0.05)",
        border:`1px solid ${hov?C.orange:"rgba(255,255,255,0.1)"}`,
        borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center",
        textDecoration:"none", transition:"all 0.2s", cursor:"pointer",
        color:hov?"#fff":C.gray, flexShrink:0,
      }}>
      {iconMap[type] ?? (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
          <path d={SVGS[type] || SVGS.github}/>
        </svg>
      )}
    </a>
  );
};

// ── BUTTON ─────────────────────────────────────────────────────────────────────
const OBtn = ({ children, onClick, outline=false, style={} }) => {
  const [hov,setHov]=useState(false);
  return (
    <button
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      onClick={onClick}
      style={{
        fontFamily:SF,fontSize:14,fontWeight:600,
        color:outline?(hov?"#fff":C.orange):"#fff",
        background:outline?"transparent":(hov?C.orangeL:C.orange),
        border:`2px solid ${C.orange}`,borderRadius:8,
        padding:"12px 28px",cursor:"pointer",
        transition:"all 0.2s",
        animation:!outline?"pulse 2.5s infinite":"none",
        ...style,
      }}>{children}</button>
  );
};

// ── NAV ───────────────────────────────────────────────────────────────────────
const Nav = ({ active, setActive }) => {
  const [sc,setSc]=useState(false);
  const [isOpen,setIsOpen]=useState(false);
  useEffect(()=>{
    const fn=()=>setSc(window.scrollY>20);
    window.addEventListener("scroll",fn);
    return ()=>window.removeEventListener("scroll",fn);
  },[]);
  return (
    <nav className="nav-container" style={{
      position:"fixed",top:0,left:0,right:0,zIndex:200,
      background:sc?"rgba(20,20,20,0.96)":"transparent",
      backdropFilter:sc?"blur(14px)":"none",
      borderBottom:sc?`1px solid ${C.border}`:"none",
      transition:"all 0.3s",
    }}>
      <div style={{fontFamily:SF,fontSize:18,fontWeight:700,color:"#fff",cursor:"pointer",letterSpacing:-0.5}}
        onClick={()=>setActive("HOME")}>
        <span style={{color:C.orange}}>25 DEVMAKER</span>
      </div>
      <button className="hamburger" onClick={()=>setIsOpen(!isOpen)}>
        {isOpen ? "✕" : "☰"}
      </button>
      <div className={`nav-links ${isOpen ? 'open' : ''}`}>
        {["HOME","ABOUT","SKILLS","PROJECTS","CONTACT"].map(it=>(
          <button key={it} onClick={()=>{setActive(it);setIsOpen(false);}} style={{
            fontFamily:SF,fontSize:13,fontWeight:500,
            color:active===it?C.orange:C.gray,
            background:"transparent",border:"none",
            padding:"8px 14px",cursor:"pointer",
            borderBottom:active===it?`2px solid ${C.orange}`:"2px solid transparent",
            transition:"all 0.2s",
          }}>{it}</button>
        ))}
      </div>
    </nav>
  );
};

// ── HERO ──────────────────────────────────────────────────────────────────────
const Hero = ({ setActive }) => {
  const [typed,setTyped]=useState("");
  const [cur,setCur]=useState(true);
  const FULL="AI Engineer and GEN AI Specialist";
  useEffect(()=>{
    let i=0;
    const t=setInterval(()=>{setTyped(FULL.slice(0,++i));if(i>=FULL.length)clearInterval(t);},60);
    const c=setInterval(()=>setCur(p=>!p),530);
    return ()=>{clearInterval(t);clearInterval(c);};
  },[]);
  return (
    <section className="hero-section" style={{
      background:C.bgDeep,
      border:`1px solid ${C.border}`,borderRadius:20,
      margin:"12px",
      boxShadow:`inset 0 0 140px rgba(232,84,26,0.04)`,
    }}>
      {/* Border glow */}
      <div style={{position:"absolute",inset:0,borderRadius:20,border:`1px solid ${C.border}`,pointerEvents:"none"}}/>

      {/* Floating cubes */}
      <Cube size={50} style={{position:"absolute",top:90,right:200}} anim="float1" delay="0s"/>
      <Cube size={30} color="#333" style={{position:"absolute",top:70,right:100,opacity:0.7}} anim="float2" delay="0.5s"/>
      <Cube size={56} style={{position:"absolute",bottom:110,right:140,opacity:0.8}} anim="float3" delay="1s"/>
      <Cube size={22} color="#444" style={{position:"absolute",bottom:70,left:320,opacity:0.5}} anim="float2" delay="0.8s"/>
      <Cube size={38} style={{position:"absolute",top:160,right:360,opacity:0.4}} anim="float1" delay="1.5s"/>

      {/* Social sidebar */}
      <div className="social-sidebar">
        <SocialIcon type="huggingface" href="https://huggingface.co/Devmaker25"/>
        <SocialIcon type="github"   href="https://github.com/25Devmaker"/>
        <SocialIcon type="linkedin"  href="https://www.linkedin.com/in/hari-kishan-devmaker"/>
      </div>

      {/* Left content */}
      <div className="hero-content">
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:22}}>
          <div className="hello-bar"/>
          <span style={{fontFamily:SF,fontSize:13,color:C.gray,letterSpacing:3}}>Hello</span>
        </div>
        <h1 style={{fontFamily:SF,fontWeight:700,fontSize:"clamp(30px,4vw,50px)",color:"#fff",lineHeight:1.2,marginBottom:14}}>
          I'm <span style={{color:C.orange}}>Hari Kishan</span>
        </h1>
        <p style={{fontFamily:SF,fontSize:14,color:C.gray,marginBottom:10,minHeight:22}}>
          {typed}<span style={{opacity:cur?1:0,color:C.orange}}>|</span>
        </p>
        <p style={{fontFamily:SF,fontSize:14,color:C.grayD,marginBottom:36,lineHeight:1.8}}>
          Brainstorming everyday with a new topic , increase in growth , building personal projects 
          less than a Year built 9+ personal projects(Comfyui workflows, LLM's Code) , 1 hackathon(Google Gemma 4 model) , 
        </p>
        <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
          <OBtn onClick={()=>setActive("PROJECTS")}>Learn more</OBtn>
          <OBtn outline onClick={()=>setActive("CONTACT")}>Contact me</OBtn>
        </div>
        <div style={{marginTop:40}}>
          <span style={{fontFamily:SF,fontSize:11,color:C.grayD,letterSpacing:3}}>↓ scroll down</span>
        </div>
      </div>

      {/* Right — avatar orb */}
      <div className="avatar-wrapper">
        <div className="avatar-orb" style={{
          background:"radial-gradient(circle at 38% 32%, #2a2a2a, #0e0e0e)",
          border:`2px solid rgba(232,84,26,0.18)`,
          boxShadow:`0 24px 70px rgba(0,0,0,0.6),0 0 50px rgba(232,84,26,0.08)`,
        }}>
          <PixelAvatar px={11}/>
          <div style={{position:"absolute",inset:-1,borderRadius:"50%",border:`2px solid ${C.orange}`,opacity:0.25}}/>
        </div>
        <Cube size={22} style={{position:"absolute",top:-12,right:-8}} anim="float2" delay="0.3s"/>
        <Cube size={16} color="#555" style={{position:"absolute",bottom:12,left:-18}} anim="float3" delay="0.7s"/>
        {/* Orbiting dot */}
        <div className="orb-orbit"/>
      </div>
    </section>
  );
};

// ── WORK CARD (sub-component to allow useState) ───────────────────────────────
const WorkCard = ({ w, i }) => {
  const [hov,setHov]=useState(false);
  return (
    <div
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        background:C.bgCard,
        border:`1px solid ${hov?C.orange:"rgba(255,255,255,0.07)"}`,
        borderRadius:10,padding:"20px 18px",
        transition:"all 0.2s",transform:hov?"translateY(-4px)":"none",
        boxShadow:hov?`0 8px 24px rgba(232,84,26,0.12)`:"none",
        animation:`popIn 0.5s ${i*0.12}s both`,
      }}>
      <h4 style={{fontFamily:SF,fontSize:15,fontWeight:600,color:"#fff",marginBottom:6}}>{w.title}</h4>
      <p style={{fontFamily:SF,fontSize:12,color:C.gray,marginBottom:14,lineHeight:1.6}}>{w.sub}</p>
      <a href={w.link} target="_blank" rel="noreferrer" style={{fontFamily:SF,fontSize:12,fontWeight:600,color:C.orange,textDecoration:"none",borderBottom:`1px solid rgba(232,84,26,0.3)`,paddingBottom:2}}>View live →</a>
    </div>
  );
};
// ── IMG WITH FALLBACK ─────────────────────────────────────────────────────────
const ImgWithFallback = ({ src, alt, hov }) => {
  const [err, setErr] = useState(false);
  return err ? (
    <div style={{
      width: "100%", height: "100%",
      background: "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 10,
    }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(232,84,26,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
      <span style={{ fontFamily: SF, fontSize: 10, color: "rgba(232,84,26,0.4)", letterSpacing: 1 }}>
        ADD SCREENSHOT
      </span>
      <span style={{ fontFamily: SF, fontSize: 9, color: "#333", letterSpacing: 0.5 }}>
        public/{src.replace("/", "")}
      </span>
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      onError={() => setErr(true)}
      style={{
        width: "100%", height: "100%", objectFit: "cover",
        transition: "transform 0.4s ease",
        transform: hov ? "scale(1.06)" : "scale(1)",
        display: "block",
      }}
    />
  );
};

// ── PROJECT CARD ──────────────────────────────────────────────────────────────
const ProjectCard = ({ project, i }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: C.bgCard,
        border: `1px solid ${hov ? C.orange : "rgba(255,255,255,0.08)"}`,
        borderRadius: 14,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        transition: "all 0.25s",
        transform: hov ? "translateY(-6px)" : "none",
        boxShadow: hov
          ? `0 16px 40px rgba(232,84,26,0.18), 0 0 0 1px rgba(232,84,26,0.2)`
          : "0 4px 16px rgba(0,0,0,0.4)",
        animation: `popIn 0.45s ${i * 0.1}s both`,
      }}>

      {/* ── Preview Image ── */}
      <div style={{ position: "relative", overflow: "hidden", height: 200, flexShrink: 0 }}>
        <ImgWithFallback
          src={project.img}
          alt={project.title}
          hov={hov}
        />
        {/* gradient overlay */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, transparent 50%, rgba(20,20,20,0.85) 100%)",
        }} />
        {/* featured badge */}
        {project.featured && (
          <span style={{
            position: "absolute", top: 12, left: 12,
            fontFamily: SF, fontSize: 10, fontWeight: 700,
            color: "#fff", letterSpacing: 1.2,
            background: C.orange, borderRadius: 4, padding: "3px 9px",
          }}>FEATURED</span>
        )}
      </div>

      {/* ── Card Body ── */}
      <div style={{ padding: "22px 20px", display: "flex", flexDirection: "column", flex: 1 }}>
        <h4 style={{
          fontFamily: SF, fontSize: 16, fontWeight: 700,
          color: "#fff", marginBottom: 8, lineHeight: 1.3,
        }}>{project.title}</h4>

        <p style={{
          fontFamily: SF, fontSize: 13, color: C.gray,
          lineHeight: 1.75, flex: 1, marginBottom: 16,
        }}>{project.desc}</p>

        {/* Tech tags */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
          {project.tags.map(tag => (
            <span key={tag.label} style={{
              fontFamily: SF, fontSize: 10, fontWeight: 600,
              color: C.orange,
              background: "rgba(232,84,26,0.1)",
              border: "1px solid rgba(232,84,26,0.25)",
              borderRadius: 20, padding: "3px 10px",
              display: "flex", alignItems: "center", gap: 4,
            }}>
              {tag.icon && <span>{tag.icon}</span>}
              {tag.label}
            </span>
          ))}
        </div>

        {/* Visit Project button */}
        <button
          onClick={() => window.open(project.link, "_blank")}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            width: "100%",
            fontFamily: SF, fontSize: 13, fontWeight: 600,
            color: hov ? "#fff" : C.gray,
            background: hov ? C.orange : "rgba(255,255,255,0.04)",
            border: `1px solid ${hov ? C.orange : "rgba(255,255,255,0.1)"}`,
            borderRadius: 8, padding: "10px 16px",
            cursor: "pointer", transition: "all 0.2s",
          }}>
          <span>Visit Project</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

// ── ABOUT ─────────────────────────────────────────────────────────────────────
const About = () => {
  const works = [
    {title:"Reactor Faceswap",   sub:"A web app that runs ReActor face swap via ComfyUI workflow on JarvisLabs GPU - built with Gradio",        link:"https://github.com/25Devmaker/Reactor-Faceswap"},
    {title:"Dev standup autopilot",sub:"An MCP-powered engineering copilot that automatically converts developer activity across GitHub and Jira into structured standup updates and team insights, eliminating manual status reporting.",       link:"https://github.com/25Devmaker/MCPservers"},
    {title:"Portfolio",   sub:"A developer portfolio showcasing  AI engineering projects, freelance services, and technical skills",  link:"https://25devmaker.vercel.app/"},
  ];
  return (
    <section className="about-section" style={{background:C.bg}}>
      <div style={{maxWidth:1000,margin:"0 auto"}}>
        <div className="about-grid">
          {/* Photo block */}
          <div style={{
            width:220,height:270,background:"#252525",
            border:`1px solid ${C.border}`,borderRadius:14,
            display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:14,
            boxShadow:`8px 8px 0 rgba(232,84,26,0.12)`,
            animation:"fadeUp 0.7s both",overflow:"hidden",
          }}>
            <PixelAvatar px={12}/>
            <span style={{fontFamily:PX,fontSize:7,color:C.orange}}>DEV_PH</span>
          </div>
          {/* Bio */}
          <div style={{animation:"fadeUp 0.7s 0.15s both"}}>
            <h2 style={{fontFamily:SF,fontSize:26,fontWeight:700,color:"#fff",marginBottom:12}}>About me</h2>
            <p style={{fontFamily:SF,fontSize:16,fontWeight:600,color:C.orange,marginBottom:14}}>
              I will Design &amp; Develop the best websites
            </p>
            <p style={{fontFamily:SF,fontSize:14,color:C.gray,lineHeight:1.85,marginBottom:14}}>
              I build AI systems that replace repetitive work and cut production costs.
              Most businesses are doing manually what should take seconds  editing product photos,ads, qualifying leads, generating content, answering the same questions. I build the tools that fix that.
              In the last year I've shipped a RAG pipeline for exam prep, an AI product photography workflow that generates studio-quality visuals from a phone photo, and a multi-tool MCP automation server. All production. All real clients.
            </p>
            <p style={{fontFamily:SF,fontSize:14,color:C.gray,lineHeight:1.85}}>
              If you have a workflow that's slow, expensive, or dependent on someone doing the same thing over and over that's exactly the problem I solve.
            </p>
          </div>
        </div>

        {/* Featured Works */}
        <div style={{animation:"fadeUp 0.7s 0.3s both"}}>
          <h3 style={{fontFamily:SF,fontSize:22,fontWeight:600,color:"#fff",marginBottom:20}}>Featured Works</h3>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:14}}>
            {works.map((w,i)=>(
              <WorkCard key={w.title} w={w} i={i}/>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ── SKILLS ─────────────────────────────────────────────────────────────────────
const Skills = () => {
  const [hov,setHov]=useState(null);
  return (
    <section className="about-section" style={{background:C.bgDeep}}>
      <div style={{maxWidth:1000,margin:"0 auto"}}>
        <h2 style={{fontFamily:SF,fontSize:26,fontWeight:700,color:"#fff",marginBottom:8,animation:"fadeUp 0.7s both"}}>My Skills</h2>
        <div style={{width:48,height:3,background:C.orange,marginBottom:44,animation:"slideIn 0.7s 0.2s both"}}/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:14,marginBottom:52}}>
          {SKILL_LIST.map((sk,i)=>(
            <div key={sk.name} onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}
              style={{
                display:"flex",flexDirection:"column",alignItems:"center",gap:10,
                padding:"18px 10px",
                background:hov===i?"rgba(232,84,26,0.1)":"rgba(255,255,255,0.03)",
                border:`1px solid ${hov===i?C.orange:"rgba(255,255,255,0.07)"}`,
                borderRadius:10,cursor:"default",
                transition:"all 0.2s",transform:hov===i?"translateY(-4px)":"none",
                animation:`popIn 0.5s ${i*0.05}s both`,
              }}>
              <SkillIcon type={sk.type} size={26} color={hov===i?C.orange:C.gray}/>
              <span style={{fontFamily:SF,fontSize:11,color:hov===i?C.orange:C.gray,fontWeight:500,textAlign:"center"}}>{sk.name}</span>
            </div>
          ))}
        </div>
        {/* Proficiency bars */}
        <h3 style={{fontFamily:SF,fontSize:18,fontWeight:600,color:"#fff",marginBottom:24}}>Proficiency</h3>
        {[
          {name:"Python & ML",pct:85},{name:"React / JS",pct:75},
          {name:"CSS / Design",pct:80},{name:"Git & DevOps",pct:72},
        ].map((bar,i)=>(
          <div key={bar.name} style={{marginBottom:18,animation:`fadeUp 0.5s ${i*0.1}s both`}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
              <span style={{fontFamily:SF,fontSize:13,color:C.gray}}>{bar.name}</span>
              <span style={{fontFamily:SF,fontSize:13,color:C.orange,fontWeight:600}}>{bar.pct}%</span>
            </div>
            <div style={{height:6,background:"rgba(255,255,255,0.06)",borderRadius:3}}>
              <div style={{width:`${bar.pct}%`,height:"100%",background:`linear-gradient(90deg,${C.orange},${C.orangeL})`,borderRadius:3}}/>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ── SKILL SVG ICON ────────────────────────────────────────────────────────────
const SkillIcon = ({ type, size=24, color=C.gray }) => {
  const path = SVGS[type];
  if (!path) return null;
  // sklearn and pandas use polyline/multi-path, render as stroke
  const isStroke = type==="sklearn"||type==="pandas"||type==="jupyter";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={isStroke?"none":"currentColor"}
      stroke={isStroke?"currentColor":"none"}
      strokeWidth={isStroke?1.5:0}
      style={{color, flexShrink:0}}>
      <path d={path}/>
    </svg>
  );
};

const SKILL_LIST = [
  {type:"html",    name:"HTML"},
  {type:"css",     name:"CSS"},
  {type:"js",      name:"JavaScript"},
  {type:"react",   name:"React"},
  {type:"python",  name:"Python"},
  {type:"sklearn", name:"sklearn"},
  {type:"pandas",  name:"Pandas"},
  {type:"jupyter", name:"Jupyter"},
  {type:"github",  name:"GitHub"},
  {type:"vscode",  name:"VS Code"},
  {type:"node",    name:"Node.js"},
  {type:"sql",     name:"SQL"},
];
// ── CURATED PROJECTS DATA ─────────────────────────────────────────────────────
// 📌 To add a screenshot: drop a PNG named after `img` into public/ folder
const PROJECTS = [
  {
    title: "Gemma4 Local Lead Qualifier",
    desc: "Qualifies interested leads from a CSV using an AI lead agent that scrapes websites — powered by Gemma 4 running fully locally.",
    img: "/proj_gemma4.png",           // 📌 drop your screenshot here
    link: "https://github.com/25Devmaker/Gemma4-Local_lead_qualifier",
    featured: true,
    tags: [{ label: "Python" }, { label: "Gemma 4" }, { label: "LLM Agent" }, { label: "Web Scraping" }],
  },
  {
    title: "Exam-pass AI",
    desc: "RAG-based AI assistant that answers exam questions from your semester PDFs — built with ChromaDB, CodeBERT and Llama 3.1 70B via Nvidia NIM. Available on Telegram @TaskNucleusBot.",
    img: "/proj_exampass.png",          // 📌 drop your screenshot here
    link: "https://github.com/25Devmaker/Exam-pass_AI",
    featured: true,
    tags: [{ label: "Python" }, { label: "RAG" }, { label: "ChromaDB" }, { label: "Llama 3.1" }],
  },
  {
    title: "LLM Tool-Call Lead Gen",
    desc: "AI-powered lead generation system using LLM tool calling with Groq, SerpAPI, and Telegram. Built in public, step-by-step.",
    img: "/proj_llmtool.png",           // 📌 drop your screenshot here
    link: "https://github.com/25Devmaker/llm-tool-call",
    featured: false,
    tags: [{ label: "Python" }, { label: "Groq" }, { label: "SerpAPI" }, { label: "Telegram" }],
  },
  {
    title: "Research2Deck",
    desc: "AI-powered research tool that turns any topic into a PowerPoint, PDF, or Word document in seconds.",
    img: "/.png",      // 📌 drop your screenshot here
    link: "https://github.com/25Devmaker/Research2Deck",
    featured: false,
    tags: [{ label: "Python" }, { label: "Generative AI" }, { label: "PPTX" }, { label: "PDF" }],
  },
  {
    title: "CourseHub",
    desc: "Online course registration platform built with Python Flask. Students sign up with OTP-verified email, enroll in courses, and track learning progress chapter by chapter.",
    img: "/proj_coursehub.png",          // 📌 drop your screenshot here
    link: "https://github.com/25Devmaker/Coursehub",
    featured: false,
    tags: [{ label: "Python" }, { label: "Flask" }, { label: "HTML/CSS" }, { label: "OTP Auth" }],
  },
  {
    title: "Reactor Faceswap",
    desc: "A web app that runs ReActor face swap via ComfyUI workflow on JarvisLabs GPU — built with Gradio.",
    img: "/proj_reactor.png",            // 📌 drop your screenshot here
    link: "https://github.com/25Devmaker/Reactor-Faceswap",
    featured: false,
    tags: [{ label: "Python" }, { label: "Gradio" }, { label: "ComfyUI" }, { label: "GPU" }],
  },
  {
    title: "Llama4 Image Caption Generator",
    desc: "Upload an image and get an AI-generated prompt you can use to recreate or inspire similar images — powered by Llama 4.",
    img: "/proj_llama4caption.png",      // 📌 drop your screenshot here
    link: "https://github.com/25Devmaker/llama4-image-caption-generator",
    featured: false,
    tags: [{ label: "Python" }, { label: "Llama 4" }, { label: "Vision AI" }, { label: "Gradio" }],
  },
  {
    title: "Portfolio Website",
    desc: "work and Professional portfolio ",
    img: "/proj_portfolio.png",          // 📌 drop your screenshot here
    link: "https://25devmaker.vercel.app/",
    featured: true,
    tags: [{ label: "React" }, { label: "JavaScript" }, { label: "EmailJS" }, { label: "Vercel" }],
  },
];

// ── PROJECTS ──────────────────────────────────────────────────────────────────
const Projects = () => {
  return (
    <section className="projects-section" style={{ background: C.bg }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 48, animation: "fadeUp 0.7s both" }}>
          <h2 style={{ fontFamily: SF, fontSize: 26, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
            Featured Works
          </h2>
          <div style={{ width: 48, height: 3, background: C.orange, marginBottom: 16 }} />
          <p style={{ fontFamily: SF, fontSize: 14, color: C.gray, maxWidth: 520, lineHeight: 1.8 }}>
            A selection of client and personal projects — from interior design platforms
            to academic tools and corporate sites.
          </p>
        </div>

        {/* Project Grid */}
        <div className="projects-grid">
          {PROJECTS.map((project, i) => (
            <ProjectCard key={project.title} project={project} i={i} />
          ))}
        </div>

        {/* GitHub CTA */}
        <div style={{
          marginTop: 52, textAlign: "center",
          padding: "32px", borderRadius: 14,
          background: "rgba(232,84,26,0.05)",
          border: "1px solid rgba(232,84,26,0.15)",
          animation: "fadeUp 0.7s 0.3s both",
        }}>
          <p style={{ fontFamily: SF, fontSize: 14, color: C.gray, marginBottom: 16 }}>
            Want to see more? Check out all my open-source work on GitHub.
          </p>
          <button
            onClick={() => window.open("https://github.com/25Devmaker", "_blank")}
            style={{
              fontFamily: SF, fontSize: 13, fontWeight: 600,
              color: "#fff", background: C.orange,
              border: "none", borderRadius: 8,
              padding: "11px 28px", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d={SVGS.github} />
            </svg>
            View GitHub Profile
          </button>
        </div>
      </div>
    </section>
  );
};


// ── SECURITY HELPERS ──────────────────────────────────────────────────────────
const sanitize = str => DOMPurify.sanitize(str.trim()).slice(0, 500);

const validators = {
  name:    v => v.trim().length >= 2,
  email:   v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
  phone:   v => v===""||/^\+?[\d\s\-().]{7,15}$/.test(v.trim()),
  message: v => v.trim().length >= 10,
};

const ERR_MSG = {
  name:    "Name must be at least 2 characters",
  email:   "Please enter a valid email address",
  phone:   "Please enter a valid phone number",
  message: "Message must be at least 10 characters",
};

// ── CONTACT ───────────────────────────────────────────────────────────────────
const Contact = () => {
  const [form,    setForm]    = useState({name:"",email:"",phone:"",location:"",message:""});
  const [honey,   setHoney]   = useState("");
  const [errors,  setErrors]  = useState({});
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [attempts,setAttempts]= useState(0);
  const [blocked, setBlocked] = useState(false);
  const [cooldown,setCooldown]= useState(false);
  const lastSubmit = useRef(0);
  const timerRef   = useRef(null);

  const validate = () => {
    const e={};
    if(!validators.name(form.name))       e.name    = ERR_MSG.name;
    if(!validators.email(form.email))     e.email   = ERR_MSG.email;
    if(!validators.phone(form.phone))     e.phone   = ERR_MSG.phone;
    if(!validators.message(form.message)) e.message = ERR_MSG.message;
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const clearErr = key => setErrors(p=>({...p,[key]:""}));

  const submit=()=>{
    // Honeypot check
    if(honey) return;
    // Rate limiting
    const now = Date.now();
    if(blocked) return;
    if(now - lastSubmit.current < 10000){
      setCooldown(true);
      setTimeout(()=>setCooldown(false), 3000);
      return;
    }
    if(attempts >= 3){
      setBlocked(true);
      timerRef.current = setTimeout(()=>{setBlocked(false);setAttempts(0);}, 300000);
      return;
    }
    if(!validate()) return;
    setLoading(true);
    setAttempts(a=>a+1);
    lastSubmit.current = now;

    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_a7i042u",
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_fvc31jk",
      {
        name:     sanitize(form.name),
        email:    form.email.trim().toLowerCase(),
        phone:    sanitize(form.phone),
        location: sanitize(form.location),
        message:  sanitize(form.message),
      },
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "2C0lMjumsQ8-n9NtH"
    )
    .then(()=>{
      setSent(true);
      setLoading(false);
      setForm({name:"",email:"",phone:"",location:"",message:""});
      setErrors({});
      setTimeout(()=>setSent(false), 4000);
    })
    .catch(()=>{
      setLoading(false);
      setErrors(p=>({...p,submit:"Failed to send. Please try again."}));
    });
  };

  useEffect(()=>()=>{ if(timerRef.current) clearTimeout(timerRef.current); },[]);

  const iBase = {
    fontFamily:SF, fontSize:14,
    background:"rgba(255,255,255,0.04)", color:"#fff",
    borderRadius:8, padding:"12px 16px", width:"100%",
    transition:"border-color 0.2s",
  };
  const iStyle = key => ({
    ...iBase,
    border:`1px solid ${errors[key]?"#ff4444":"rgba(255,255,255,0.1)"}`,
  });

  const fields = [
    {key:"name",     ph:"Your Full Name",  label:"Full Name", type:"text"},
    {key:"email",    ph:"your@email.com",  label:"Email",     type:"email"},
    {key:"phone",    ph:"+1 234 567 890",  label:"Phone",     type:"tel"},
    {key:"location", ph:"Your City",       label:"Location",  type:"text"},
  ];

  return (
    <section className="contact-section" style={{background:C.bgDeep}}>
      <div className="contact-main-grid">

        {/* Left info */}
        <div style={{animation:"fadeUp 0.7s both"}}>
          <h2 style={{fontFamily:SF,fontSize:26,fontWeight:700,color:"#fff",marginBottom:8}}>Get a quote</h2>
          <div style={{width:48,height:3,background:C.orange,marginBottom:24}}/>
          <p style={{fontFamily:SF,fontSize:14,color:C.gray,lineHeight:1.85,marginBottom:32}}>
            Have a project in mind? Want to collaborate or just say hi?
            Drop me a message and I'll get back to you as soon as possible.
          </p>
          {[
            {type:"email",    label:"Email",    val:"harikishan0058@email.com"},
            {type:"location", label:"Location", val:"Bengaluru, India"},
            {type:"github",   label:"GitHub",   val:"github.com/25Devmaker"},
          ].map(c=>(
            <div key={c.label} style={{display:"flex",gap:14,alignItems:"flex-start",marginBottom:18}}>
              <div style={{width:36,height:36,background:"rgba(232,84,26,0.1)",border:`1px solid rgba(232,84,26,0.2)`,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:C.orange}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d={SVGS[c.type]}/></svg>
              </div>
              <div>
                <div style={{fontFamily:SF,fontSize:11,color:C.grayD,marginBottom:2}}>{c.label}</div>
                <div style={{fontFamily:SF,fontSize:13,color:"#fff"}}>{c.val}</div>
              </div>
            </div>
          ))}
          <div style={{display:"flex",gap:10,marginTop:28}}>
            <SocialIcon type="github"    href="https://github.com/25Devmaker"/>
            <SocialIcon type="huggingface"   href="https://huggingface.co/Devmaker25"/>
            <SocialIcon type="linkedin"  href="https://www.linkedin.com/in/hari-kishan-devmaker"/>
          </div>
          <div style={{marginTop:32}}><PixelRobot px={8}/></div>

          {/* Security badges */}
          <div style={{marginTop:28,display:"flex",gap:8,flexWrap:"wrap"}}>
            {["Spam protected","Rate limited","XSS sanitized"].map(b=>(
              <span key={b} style={{fontFamily:SF,fontSize:10,color:C.orange,background:"rgba(232,84,26,0.08)",border:`1px solid rgba(232,84,26,0.2)`,borderRadius:20,padding:"4px 10px"}}>
                ✓ {b}
              </span>
            ))}
          </div>
        </div>

        {/* Right form */}
        {sent ? (
          <div style={{background:C.bgCard,border:`1px solid ${C.orange}`,borderRadius:12,padding:40,textAlign:"center",boxShadow:`0 0 40px rgba(232,84,26,0.15)`,animation:"popIn 0.4s both"}}>
            <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}>
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>
              </svg>
            </div>
            <h3 style={{fontFamily:SF,fontSize:20,fontWeight:600,color:"#fff",marginBottom:8}}>Message Sent!</h3>
            <p style={{fontFamily:SF,fontSize:14,color:C.gray}}>I'll get back to you soon.</p>
          </div>
        ) : (
          <div style={{background:C.bgCard,border:`1px solid rgba(255,255,255,0.07)`,borderRadius:12,padding:32,animation:"fadeUp 0.7s 0.15s both"}}>

            {/* Honeypot — hidden from humans, traps bots */}
            <input
              type="text" value={honey}
              onChange={e=>setHoney(e.target.value)}
              style={{display:"none"}} tabIndex="-1" autoComplete="off"
            />

            {/* Blocked warning */}
            {blocked && (
              <div style={{background:"rgba(255,68,68,0.1)",border:"1px solid #ff4444",borderRadius:8,padding:"12px 16px",marginBottom:16,fontFamily:SF,fontSize:13,color:"#ff4444"}}>
                Too many attempts. Please wait 5 minutes.
              </div>
            )}

            {/* Cooldown warning */}
            {cooldown && (
              <div style={{background:"rgba(232,84,26,0.1)",border:`1px solid ${C.orange}`,borderRadius:8,padding:"12px 16px",marginBottom:16,fontFamily:SF,fontSize:13,color:C.orange}}>
                Please wait a moment before sending again.
              </div>
            )}

            {/* Attempt counter */}
            {attempts > 0 && (
              <div style={{fontFamily:SF,fontSize:11,color:C.grayD,textAlign:"right",marginBottom:12}}>
                {3 - attempts} attempt{3-attempts!==1?"s":""} remaining
              </div>
            )}

            {/* Input grid */}
            <div className="form-grid">
              {fields.map(f=>(
                <div key={f.key}>
                  <div style={{fontFamily:SF,fontSize:11,color:errors[f.key]?"#ff4444":C.gray,marginBottom:6}}>{f.label}</div>
                  <input
                    type={f.type} placeholder={f.ph} value={form[f.key]}
                    style={iStyle(f.key)}
                    onChange={e=>{setForm(p=>({...p,[f.key]:e.target.value}));clearErr(f.key);}}
                    onFocus={e=>e.target.style.borderColor=errors[f.key]?"#ff4444":C.orange}
                    onBlur={e=>e.target.style.borderColor=errors[f.key]?"#ff4444":"rgba(255,255,255,0.1)"}
                  />
                  {errors[f.key]&&(
                    <span style={{fontFamily:SF,fontSize:11,color:"#ff4444",marginTop:4,display:"block"}}>
                      ⚠ {errors[f.key]}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Message textarea */}
            <div style={{marginBottom:20}}>
              <div style={{fontFamily:SF,fontSize:11,color:errors.message?"#ff4444":C.gray,marginBottom:6}}>
                Type your message <span style={{color:C.grayD}}>(min 10 chars)</span>
              </div>
              <textarea
                rows={4} placeholder="Tell me about your project..."
                value={form.message}
                style={{...iStyle("message"),resize:"vertical"}}
                onChange={e=>{setForm(p=>({...p,message:e.target.value}));clearErr("message");}}
                onFocus={e=>e.target.style.borderColor=errors.message?"#ff4444":C.orange}
                onBlur={e=>e.target.style.borderColor=errors.message?"#ff4444":"rgba(255,255,255,0.1)"}
              />
              <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                {errors.message
                  ? <span style={{fontFamily:SF,fontSize:11,color:"#ff4444"}}>⚠ {errors.message}</span>
                  : <span/>
                }
                <span style={{fontFamily:SF,fontSize:11,color:C.grayD}}>{form.message.length}/500</span>
              </div>
            </div>

            {/* Submit error */}
            {errors.submit&&(
              <div style={{background:"rgba(255,68,68,0.1)",border:"1px solid #ff4444",borderRadius:8,padding:"10px 14px",marginBottom:14,fontFamily:SF,fontSize:13,color:"#ff4444"}}>
                ⚠ {errors.submit}
              </div>
            )}

            {/* Submit button */}
            <OBtn
              onClick={submit}
              style={{width:"100%",display:"block",textAlign:"center",opacity:loading||blocked?0.6:1}}
            >
              {loading ? "Sending..." : blocked ? "Blocked (5 min)" : "Send message →"}
            </OBtn>
          </div>
        )}
      </div>
    </section>
  );
};

// ── TICKER ─────────────────────────────────────────────────────────────────────
const Ticker = () => {
  const items=["REACT","PYTHON","MACHINE LEARNING","CODEALPHA INTERN","SKLEARN","OPEN SOURCE","GITHUB: 25DEVMAKER","FULL STACK DEV"];
  const txt=items.join("   ·   ");
  return (
    <div style={{background:C.orange,overflow:"hidden",height:36,display:"flex",alignItems:"center"}}>
      <div style={{display:"flex",animation:"marquee 22s linear infinite",whiteSpace:"nowrap"}}>
        {[txt,txt].map((t,i)=><span key={i} style={{fontFamily:SF,fontSize:11,fontWeight:600,color:"#fff",padding:"0 30px",letterSpacing:2}}>{t}</span>)}
      </div>
    </div>
  );
};

// ── APP ───────────────────────────────────────────────────────────────────────
export default function Portfolio() {
  const [active,setActive]=useState("HOME");
  const S={HOME:Hero,ABOUT:About,SKILLS:Skills,PROJECTS:Projects,CONTACT:Contact};
  const Section=S[active];
  return (
    <div style={{minHeight:"100vh",position:"relative"}}>
      <style>{GLOBAL}</style>
      <Nav active={active} setActive={setActive}/>
      <div style={{paddingTop:active==="HOME"?0:64}}>
        <Section setActive={setActive}/>
      </div>
      <Ticker/>
      <div className="footer-flex" style={{background:C.bgDeep,borderTop:`1px solid ${C.border}`}}>
        <span style={{fontFamily:SF,fontSize:13,color:C.grayD}}>© 2025 Dev_Placeholder · All rights reserved.</span>
        <span style={{fontFamily:SF,fontSize:13,color:C.grayD}}>Built with <span style={{color:C.orange}}>React</span> <svg style={{display:"inline",verticalAlign:"middle"}} width="13" height="13" viewBox="0 0 24 24" fill={C.orange}><path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z"/></svg></span>
      </div>
    </div>
  );
}